//* admin 전용 주문 상품 교체 모달 — 결제 후 상위 상품으로 변경하고 차액을 수기 수취한 경우의 정정 처리
//* 후보 상품은 주문의 bugo 기준 판매가능 목록(bugoBrand rebate + 유통망 매칭) 중 같은 shop 것만.
//* 비활성(isEnabled=false) 상품 포함은 의도: 고객에게 숨긴 지역별 고가 변형을 교체에 쓰기 위함.
//* (스펙: docs/shop-order-change-item-spec.md · 404 안내는 구서버 안전장치)

import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { adminShopItemListByBugoForShopUrl } from '@shared/api/shopItem/shopItem.hook';
import { ShopItem } from '@shared/api/shopItem/shopItem.interface';
import { ShopItemInfo } from '@shared/api/shopItemInfo/shopItemInfo.interface';
import {
  adminChangeShopOrderItem,
  ShopOrderItemChange,
} from '@shared/api/shopOrder/shopOrder.controller';
import { shopOrderListForSellerUrl } from '@shared/api/shopOrder/shopOrder.hook';
import { ShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import Modal from '@shared/components/Modal';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { api_ } from '@shared/plugIn/axios';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { useQueryClient } from 'react-query';

interface Props {
  openId: string;
  order: ShopOrderForSeller;
}

type CandidateItem = ShopItem<ShopItemInfo>;

const statusOf = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;

const changeErrorMessage = (e: unknown): string => {
  switch (statusOf(e)) {
    case 404:
      return '주문/상품을 찾을 수 없거나 아직 서버에 반영되지 않은 기능입니다.';
    case 400:
      return '교체할 수 없는 상품이거나 요청이 올바르지 않습니다.';
    case 403:
      return '관리자만 사용할 수 있는 기능입니다.';
    case 409:
      return '주문 상태가 방금 바뀌어(취소·정산 등) 교체하지 못했습니다. 새로고침 후 다시 확인해주세요.';
    default:
      return '상품 교체에 실패했습니다.';
  }
};

//* 도매가 2종 — 도매표기(shopItemInfo.priceWhole): 57,500 대신 60,000처럼 헷갈림 방지용
//* 반올림 표기값. 실도매(shopItem.priceWhole): 정산·장부에 실제 쓰는 값(없으면 표기값) —
//* embed 매핑(priceWhole = shopItem ?? shopItemInfo)과 동일 규칙.
const displayWholesaleOf = (item: CandidateItem): number | undefined =>
  item.shopItemInfo?.priceWhole;
const actualWholesaleOf = (item: CandidateItem): number | undefined =>
  item.priceWhole ?? item.shopItemInfo?.priceWhole;

const won = (n: number | undefined | null): string =>
  typeof n === 'number' ? `${n.toLocaleString()}원` : '-';

export const OrderItemChangeModal: FC<Props> = function OrderItemChangeModal({
  openId,
  order,
}: Props) {
  const { sellerRole } = useAuth();
  const [open, setOpen] = useRecoilState(simpleOpenAtomFamily(openId));
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const queryClient = useQueryClient();

  const currentShopItem = order.orderDetail.shopItem;
  const currentEmbed = order.orderDetail.shopItemEmbed;
  const quantity = order.orderDetail.quantity ?? 1;
  //* 후보 목록의 출처(부고브랜드 유통망) 표기용 — 예: 프리드
  const brandName = order.bugo?.bugoBrand?.bugoAgency?.nickName;
  //* 현재까지 받은 금액 (현금 + 포인트)
  const paidTotal =
    (order.paymentDetail.paymentByCash ?? 0) + (order.paymentDetail.paymentByPoint ?? 0);

  const onCloseModal = useCallback(() => {
    setOpen(false);
    history.back();
  }, [setOpen]);

  //* 교체 후보 상품 목록 (모달 열릴 때 조회)
  const [items, setItems] = useState<CandidateItem[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [additionalPayment, setAdditionalPayment] = useState<string>('');
  const [memo, setMemo] = useState<string>('');
  //* 계좌로 받은 차액의 현금영수증 — 입금확인이 곧 교체 시점이라 그 자리에서 발행하는
  //* 흐름이 자연스럽다. 기본값은 끄기: 실제로 발행하지 않았는데 도장부터 찍히면
  //* 큐에서 사라져 영영 누락된다(반대는 큐에 남을 뿐이라 회복된다).
  const [cashReceiptIssued, setCashReceiptIssued] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const bugoId = order.bugo?._id;
  const shopId = typeof order.shop === 'string' ? order.shop : undefined;

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setAdditionalPayment('');
    setMemo('');
    if (!bugoId || !shopId) {
      setListError('부고/꽃집 정보가 없는 주문은 상품 교체를 지원하지 않습니다.');
      return;
    }
    setItems(null);
    setListError(null);
    api_
      .get<CandidateItem[]>(adminShopItemListByBugoForShopUrl(bugoId, shopId))
      .then(({ data }) => setItems(data))
      .catch((err) => {
        console.error(err);
        setListError('판매가능 상품 목록을 불러오지 못했습니다.');
      });
  }, [open, bugoId, shopId]);

  const candidates = useMemo(
    () => (items ?? []).filter((item) => item._id !== currentShopItem?._id),
    [items, currentShopItem?._id],
  );

  const selectedItem = useMemo(
    () => candidates.find((item) => item._id === selectedId) ?? null,
    [candidates, selectedId],
  );

  //* 상품 선택 시 차액 기본값 = 새 판매가 × 수량 − 현재 결제합계
  const onSelectItem = (item: CandidateItem) => {
    setSelectedId(item._id);
    setAdditionalPayment(String(Math.max(0, item.priceRetail * quantity - paidTotal)));
  };

  const expectedDiff = selectedItem ? selectedItem.priceRetail * quantity - paidTotal : 0;
  const additionalPaymentNumber = Number(additionalPayment || 0);
  const diffMismatch = selectedItem !== null && additionalPaymentNumber !== expectedDiff;

  const onSubmit = useCallback(async () => {
    if (!selectedItem) {
      notificateError(notifiacationInstance, '교체할 상품을 선택해주세요.');
      return;
    }
    if (!Number.isInteger(additionalPaymentNumber) || additionalPaymentNumber < 0) {
      notificateError(notifiacationInstance, '추가 수취금액은 0 이상의 정수여야 합니다.');
      return;
    }
    setSubmitting(true);
    try {
      const dto: ShopOrderItemChange = {
        shopItemId: selectedItem._id,
        additionalPaymentByCash: additionalPaymentNumber,
        memo: memo.trim() || undefined,
        //* 차액 0원이면 발행할 게 없다 — 서버도 무시하지만 보내지 않는 편이 명확하다
        ...(cashReceiptIssued && additionalPaymentNumber > 0
          ? { cashReceiptIssued: true }
          : {}),
      };
      await adminChangeShopOrderItem(order._id, dto);
      queryClient.invalidateQueries(shopOrderListForSellerUrl(sellerRole()));
      notificateSuccess(notifiacationInstance, '상품 교체 완료');
      onCloseModal();
    } catch (err) {
      console.error(err);
      notificateError(notifiacationInstance, changeErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }, [
    selectedItem,
    additionalPaymentNumber,
    memo,
    cashReceiptIssued,
    order._id,
    queryClient,
    sellerRole,
    notifiacationInstance,
    onCloseModal,
  ]);

  return (
    <Modal openId={openId}>
      <div className="space-y-4">
        <div>
          <p className="text-lg font-bold">주문 상품 교체 (관리자)</p>
          <p className="break-keep pt-1 text-sm text-gray-600">
            결제 후 상위 상품으로 변경하고 차액을 따로 받은 경우의 정정 처리입니다. 주문의
            상품·가격정보(정산/리베이트 기준 포함)와 결제금액이 함께 변경됩니다.
          </p>
        </div>

        {/* 현재 상품 */}
        <div className="rounded-md border bg-gray-50 p-3 text-sm">
          <p className="font-medium">
            현재: {currentEmbed?.name ?? currentShopItem?.shopItemInfo?.name}
          </p>
          <p className="text-gray-700">
            판매가 {won(currentEmbed?.priceRetail ?? 0)} · 결제합계 {won(paidTotal)}
            {quantity > 1 ? ` · 수량 ${quantity}` : ''}
          </p>
          <p className="text-gray-700">
            도매표기 {won(currentShopItem?.shopItemInfo?.priceWhole)} · 실도매{' '}
            {won(currentEmbed?.priceWhole)}
            <span className="text-xs text-gray-500"> (정산 기준)</span>
          </p>
        </div>

        {/* 교체 후보 목록 */}
        <div className="space-y-2">
          <p className="text-sm font-bold">
            교체할 상품 선택
            {brandName && (
              <span className="pl-1 font-normal text-cyan-700">
                — {brandName} 연결상품
              </span>
            )}
          </p>
          <p className="break-keep text-xs text-gray-500">
            {brandName ? `${brandName} 부고브랜드` : '주문 부고브랜드'}의 유통망에 연결된
            판매상품 중 같은 꽃집 것만 표시됩니다. 고객에게 숨긴 비활성 상품(지역별 가격
            변형)도 포함됩니다. 도매표기 = 헷갈림 방지용 반올림 표기값, 실도매 =
            정산·장부에 쓰는 실제 도매가.
          </p>
          {listError ? (
            <p className="text-sm text-red-600">{listError}</p>
          ) : items === null ? (
            <Loading />
          ) : candidates.length === 0 ? (
            <p className="text-sm text-gray-500">교체 가능한 다른 상품이 없습니다.</p>
          ) : (
            <div className="max-h-60 space-y-1 overflow-y-auto">
              {candidates.map((item) => {
                const selected = item._id === selectedId;
                return (
                  <button
                    key={item._id}
                    type="button"
                    className={`flex w-full items-center justify-between rounded-md border p-2 text-left text-sm ${
                      selected
                        ? 'border-cyan-600 bg-cyan-50 font-bold'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => onSelectItem(item)}
                  >
                    <span className="truncate pr-2">
                      {item.name ?? item.shopItemInfo?.name}
                    </span>
                    <span className="whitespace-nowrap text-right">
                      <span className="block">판매 {won(item.priceRetail)}</span>
                      <span
                        className={`block text-xs font-normal ${
                          selected ? 'text-cyan-700' : 'text-gray-500'
                        }`}
                      >
                        도매표기 {won(displayWholesaleOf(item))} · 실도매{' '}
                        {won(actualWholesaleOf(item))}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 차액 및 메모 */}
        {selectedItem && (
          <div className="space-y-2 rounded-md border p-3 text-sm">
            <p>
              새 판매가 {(selectedItem.priceRetail * quantity).toLocaleString()}원 −
              결제합계 {paidTotal.toLocaleString()}원 ={' '}
              <span className="font-bold">차액 {expectedDiff.toLocaleString()}원</span>
            </p>
            <p className="text-xs text-gray-600">
              실도매(정산 기준) {won(currentEmbed?.priceWhole)} →{' '}
              {won(actualWholesaleOf(selectedItem))} — 꽃집 정산 기준도 함께 변경됩니다.
            </p>
            <div className="flex items-center gap-2">
              <label className="whitespace-nowrap font-medium">추가 수취금액</label>
              <input
                type="number"
                min={0}
                className="w-32 rounded border border-gray-300 px-2 py-1 text-right"
                value={additionalPayment}
                onChange={(e) => setAdditionalPayment(e.target.value)}
              />
              <span>원</span>
            </div>
            {diffMismatch && (
              <p className="break-keep text-xs text-amber-600">
                계산된 차액({expectedDiff.toLocaleString()}원)과 다릅니다. 의도한 금액인지
                확인해주세요.
              </p>
            )}
            <input
              type="text"
              className="w-full rounded border border-gray-300 px-2 py-1"
              placeholder="메모 (ex. 가상계좌 이체 35,000원 수취)"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
            />
            {/* 계좌로 받은 차액은 PG가 발행해주지 않는다 — 여기서 처리했으면 도장까지,
                아니면 그냥 두면 현금영수증 발행 대기 큐에 남아 나중에 일괄 처리된다 */}
            {additionalPaymentNumber > 0 && (
              <label className="flex cursor-pointer items-start gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={cashReceiptIssued}
                  onChange={(e) => setCashReceiptIssued(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="break-keep">
                  현금영수증도 지금 발행함 (홈택스에서 차액{' '}
                  {additionalPaymentNumber.toLocaleString()}원을 발행한 경우에만 체크)
                  <span className="mt-0.5 block text-[11px] text-gray-400">
                    체크하지 않으면 현금영수증 발행 대기 목록에 남습니다.
                  </span>
                </span>
              </label>
            )}
          </div>
        )}

        <Button
          className="button-rectangle h-10 theme-text-4 theme-bg-main"
          disabled={submitting || !selectedItem}
          onClick={onSubmit}
        >
          {submitting ? '처리 중...' : '상품 교체'}
        </Button>
      </div>
    </Modal>
  );
};
